import { memo, useCallback, useMemo, useState } from 'react';
import { Polyline, Marker, Tooltip } from 'react-leaflet';
import { DivIcon, LeafletMouseEvent } from 'leaflet';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTheme } from '../../hooks/useTheme';
import { RouteSegment } from '../../utils/route';
import { DistanceUnit, formatDistanceUnit } from '../../utils/distance';
import { formatLatLng, formatHeading } from '../../utils/formatters';
import { useClusters } from '../../hooks/useClusters';
import { useRouteMetrics } from '../../hooks/useRouteMetrics';
import {
  LegPointerHandlers,
  useLegDragInsert,
} from '../../hooks/useLegDragInsert';

/**
 * The point that rides under the cursor on a hovered leg and follows a drag.
 * Built once at module scope: a DivIcon rebuilt mid-gesture makes react-leaflet
 * swap the DOM element under the pointer, so emphasis lives in CSS (#433).
 */
const GRAB_ICON = new DivIcon({
  html: '<div class="planner-leg-grab-dot" role="presentation"></div>',
  className: 'planner-leg-grab',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

/** Theme-derived colours shared by every leg. */
interface LegColors {
  base: string;
  hot: string;
  labelBg: string;
  labelFg: string;
}

interface RouteLegProps {
  /** Leg position — also where a split on this leg is inserted after. */
  index: number;
  segment: RouteSegment;
  unit: DistanceUnit;
  /** Highlighted, because it is hovered or touches the hovered waypoint. */
  isHot: boolean;
  /** Both endpoints sit behind one cluster badge — no room for a label. */
  collapsed: boolean;
  colors: LegColors;
  onHover: (index: number | null) => void;
  handlers: LegPointerHandlers;
}

/**
 * One leg of the route: the line, its distance label, and the hover/press
 * wiring that makes it grabbable.
 *
 * Memoised because the pointer riding along a leg re-renders the parent on
 * every `mousemove`, and rebuilding every label's DivIcon at that rate would
 * make a long route crawl. Every prop is either a value or a stable callback,
 * so a leg only re-renders when its own geometry, label or highlight changes.
 */
const RouteLeg = memo<RouteLegProps>(
  ({ index, segment, unit, isHot, collapsed, colors, onHover, handlers }) => {
    const start = useMemo<[number, number]>(
      () => [segment.from.lat, segment.from.lng],
      [segment.from.lat, segment.from.lng]
    );
    const end = useMemo<[number, number]>(
      () => [segment.to.lat, segment.to.lng],
      [segment.to.lat, segment.to.lng]
    );
    const midpoint: [number, number] = [
      (start[0] + end[0]) / 2,
      (start[1] + end[1]) / 2,
    ];

    const { trackPointer, releasePointer, beginDrag } = handlers;
    const legHandlers = useMemo(
      () => ({
        mouseover: () => onHover(index),
        mousemove: (e: LeafletMouseEvent) =>
          trackPointer(index, e, start, end),
        mouseout: () => {
          onHover(null);
          releasePointer(index);
        },
        mousedown: (e: LeafletMouseEvent) => beginDrag(index, e),
      }),
      [beginDrag, end, index, onHover, releasePointer, start, trackPointer]
    );

    // Always-visible per-segment distance label. Non-interactive so it does
    // not steal hover from the polyline beneath it.
    //
    // The icon box is zero-sized so Leaflet pins it exactly on the midpoint;
    // the plate then sizes itself to the text and centres on that point,
    // sitting just above the leg so it clears the grab point. Without
    // `position`/`width`/`transform` the plate collapses to its padding and
    // the text spills out unbacked (#433).
    const labelIcon = useMemo(
      () =>
        new DivIcon({
          html: `
            <div style="
              position: absolute;
              width: max-content;
              transform: translate(-50%, -160%);
              background: ${colors.labelBg};
              color: ${colors.labelFg};
              border: 1px solid ${isHot ? colors.hot : colors.base};
              border-radius: 4px;
              padding: 1px 5px;
              font-size: 10px;
              font-weight: 600;
              white-space: nowrap;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            ">${formatDistanceUnit(segment.distanceKm, unit)}</div>
          `,
          className: 'planner-segment-label',
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
      [colors, isHot, segment.distanceKm, unit]
    );

    return (
      <>
        <Polyline
          positions={[start, end]}
          color={isHot ? colors.hot : colors.base}
          weight={isHot ? 6 : 3}
          opacity={0.85}
          className={`planner-route-leg${
            segment.closing ? ' planner-route-leg-closing' : ''
          }`}
          eventHandlers={legHandlers}
        >
          <Tooltip sticky>
            <div>
              <strong>
                {segment.closing
                  ? `Leg ${segment.from.order} → home`
                  : `Leg ${segment.from.order} → ${segment.to.order}`}
              </strong>
              <br />
              From: {formatLatLng(segment.from.lat, segment.from.lng)}
              <br />
              To: {formatLatLng(segment.to.lat, segment.to.lng)}
              <br />
              Distance: {formatDistanceUnit(segment.distanceKm, unit)}
              <br />
              Bearing: {formatHeading(segment.bearingDeg)}
              <br />
              Cumulative: {formatDistanceUnit(segment.cumulativeKm, unit)}
            </div>
          </Tooltip>
        </Polyline>
        {!collapsed && (
          <Marker position={midpoint} icon={labelIcon} interactive={false} />
        )}
      </>
    );
  }
);
RouteLeg.displayName = 'RouteLeg';

/**
 * Renders the route as one Polyline per leg so each segment can carry an
 * always-visible distance label, highlight on hover, and be grabbed anywhere
 * along its length to insert a waypoint. Splitting the route (vs a single
 * Polyline) is what makes per-segment interaction possible.
 *
 * The loop-closing leg back to home is just another segment here (#404): same
 * colour, same weight, same labels, same interactions. It used to be drawn by a
 * separate dashed layer that could not be measured or edited, which made one
 * route read as two unrelated paths.
 */
export const RouteSegments: React.FC = () => {
  const { isDarkMode } = useTheme();
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );
  const unit = useSelector(
    (state: RootState) => state.missionPlanner.distanceUnit
  );
  // Waypoint hovered in the list — highlight the legs touching it (#295).
  const hoveredWaypointId = useSelector(
    (state: RootState) => state.missionPlanner.hoveredWaypointId
  );
  const placementMode = useSelector(
    (state: RootState) => state.missionPlanner.placementMode
  );
  const activeShapeTool = useSelector(
    (state: RootState) => state.missionPlanner.activeShapeTool
  );
  const [hovered, setHovered] = useState<number | null>(null);
  const onHover = useCallback((index: number | null) => setHovered(index), []);

  const { segments } = useRouteMetrics();

  // Placing waypoints or drawing a shape owns the pointer; grabbing the route
  // would fight those gestures for the same press.
  const editable = placementMode === 'idle' && activeShapeTool === null;
  const { grab, handlers } = useLegDragInsert(editable);
  const grabbed = grab ? segments[grab.index] : undefined;

  // Legs whose endpoints have collapsed into one cluster badge (#434): their
  // labels would stack on the badge, which is the pile-up clustering exists to
  // remove.
  const candidates = useMemo(
    () =>
      waypoints.map((wp) => ({
        id: wp.id,
        lat: wp.lat,
        lng: wp.lng,
        item: wp.id,
      })),
    [waypoints]
  );
  const clusters = useClusters<string>(candidates);
  const clusterOfWaypoint = useMemo(() => {
    const owner = new Map<string, string>();
    for (const cluster of clusters) {
      for (const id of cluster.items) {
        owner.set(id, cluster.key);
      }
    }
    return owner;
  }, [clusters]);

  const colors = useMemo<LegColors>(
    () => ({
      base: isDarkMode ? '#90caf9' : '#1976d2',
      hot: isDarkMode ? '#ffb74d' : '#ef6c00',
      labelBg: isDarkMode ? '#2d2d2d' : '#ffffff',
      labelFg: isDarkMode ? '#e0e0e0' : '#212121',
    }),
    [isDarkMode]
  );

  return (
    <>
      {segments.map((seg, i) => {
        // Both ends behind the same badge — nothing legible to label.
        const fromCluster = clusterOfWaypoint.get(seg.from.id);
        const collapsed =
          fromCluster !== undefined &&
          fromCluster === clusterOfWaypoint.get(seg.to.id);
        const adjacentToHovered =
          hoveredWaypointId !== null &&
          (seg.from.id === hoveredWaypointId ||
            seg.to.id === hoveredWaypointId);

        return (
          <RouteLeg
            key={`${seg.from.id}-${seg.to.id}`}
            index={i}
            segment={seg}
            unit={unit}
            isHot={hovered === i || adjacentToHovered}
            collapsed={collapsed}
            colors={colors}
            onHover={onHover}
            handlers={handlers}
          />
        );
      })}

      {/* Grab point: rests on the line under the cursor, follows a drag. */}
      {grab && (
        <Marker
          position={[grab.lat, grab.lng]}
          icon={GRAB_ICON}
          interactive={false}
        />
      )}

      {/* Where the split would land, drawn while the drag is held. */}
      {grab?.dragging && grabbed && (
        <Polyline
          positions={[
            [grabbed.from.lat, grabbed.from.lng],
            [grab.lat, grab.lng],
            [grabbed.to.lat, grabbed.to.lng],
          ]}
          color={colors.hot}
          weight={3}
          opacity={0.9}
          dashArray="6, 6"
          interactive={false}
        />
      )}
    </>
  );
};
